import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotesStore, useUIStore, useAuthStore } from '../store';
import { Search, FileText, Plus, Settings, LayoutGrid, Trash2, Star, Clock, BookOpen, Tag, FolderOpen, Download, Upload, Brain } from 'lucide-react';

const COMMANDS = [
  { id: 'new-note', label: 'New Note', icon: Plus, action: 'navigate', path: '/' },
  { id: 'graph', label: 'Graph View', icon: LayoutGrid, action: 'navigate', path: '/graph' },
  { id: 'settings', label: 'Settings', icon: Settings, action: 'navigate', path: '/settings' },
  { id: 'trash', label: 'Trash', icon: Trash2, action: 'navigate', path: '/trash' },
  { id: 'templates', label: 'Templates', icon: BookOpen, action: 'navigate', path: '/templates' },
  { id: 'starred', label: 'Starred Notes', icon: Star, action: 'navigate', path: '/?starred=true' },
  { id: 'recent', label: 'Recent Notes', icon: Clock, action: 'navigate', path: '/recent' },
  { id: 'export', label: 'Export Notes', icon: Download, action: 'export' },
  { id: 'import', label: 'Import Notes', icon: Upload, action: 'import' },
  { id: 'toggle-ai', label: 'Toggle AI Panel', icon: Brain, action: 'toggleAi' },
];

export default function CommandPalette() {
  const navigate = useNavigate();
  const { commandPaletteOpen, toggleCommandPalette, addToast } = useUIStore();
  const { notes, fetchNotes, search, searchResults, clearSearch } = useNotesStore();
  const { logout } = useAuthStore();
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); toggleCommandPalette(); }
      if (e.key === 'Escape' && commandPaletteOpen) toggleCommandPalette();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [commandPaletteOpen]);

  useEffect(() => {
    if (commandPaletteOpen) {
      setQuery('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [commandPaletteOpen]);

  useEffect(() => {
    if (query.length > 0) search(query);
    else clearSearch();
  }, [query]);

  const allItems = [
    ...COMMANDS.map(c => ({ ...c, type: 'command' })),
    ...(searchResults || []).map(n => ({ id: n.id, label: n.title, icon: FileText, type: 'note', path: `/note/${n.id}` }))
  ];

  const filtered = query
    ? allItems.filter(i => i.label.toLowerCase().includes(query.toLowerCase()))
    : COMMANDS;

  const handleSelect = (item) => {
    if (item.action === 'navigate' || item.path) navigate(item.path);
    else if (item.action === 'toggleAi') useUIStore.getState().toggleAiPanel();
    else if (item.action === 'export') handleExport();
    toggleCommandPalette();
  };

  const handleExport = async () => {
    try {
      const data = await api.exportNotes();
      const blob = new Blob([JSON.stringify(data)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `knowledge-export-${Date.now()}.json`; a.click();
      addToast({ title: 'Exported successfully', type: 'success' });
    } catch (e) { addToast({ title: 'Export failed', type: 'error' }); }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIndex(i => Math.min(i + 1, filtered.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIndex(i => Math.max(i - 1, 0)); }
    if (e.key === 'Enter' && filtered[selectedIndex]) handleSelect(filtered[selectedIndex]);
  };

  if (!commandPaletteOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh]">
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={toggleCommandPalette} />
      <div className="relative w-full max-w-lg bg-popover border rounded-xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95">
        <div className="flex items-center gap-3 px-4 py-3 border-b">
          <Search size={18} className="text-muted-foreground" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setSelectedIndex(0); }}
            onKeyDown={handleKeyDown}
            placeholder="Type a command or search notes..."
            className="flex-1 bg-transparent text-sm focus:outline-none"
          />
          <kbd className="text-xs bg-muted px-1.5 py-0.5 rounded border text-muted-foreground">ESC</kbd>
        </div>

        <div className="max-h-80 overflow-y-auto p-1">
          {filtered.length === 0 && (
            <div className="px-4 py-8 text-center text-muted-foreground text-sm">No results found</div>
          )}
          {filtered.map((item, i) => (
            <button
              key={item.id}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                i === selectedIndex ? 'bg-accent text-accent-foreground' : 'hover:bg-muted'
              }`}
              onClick={() => handleSelect(item)}
              onMouseEnter={() => setSelectedIndex(i)}
            >
              <item.icon size={16} className="text-muted-foreground" />
              <span className="flex-1 text-left">{item.label}</span>
              {item.type === 'note' && <span className="text-xs text-muted-foreground">Note</span>}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
